/**
 * GFG-273 (Campaigns tab, GFG Admin) — the personalised gift email one recipient was sent, opened
 * from the contacts table's eye icon. The body is rendered by `EmailPreview`, the same renderer
 * the campaign builder's Design Step and the Send Queue's preview already share, because the
 * endpoint answers with that exact payload shape; this tab adds no second email layout.
 *
 * `onClose` closes ONLY this dialog. The contacts modal underneath owns its own close handler and
 * stays open, which is the whole point of previewing from inside it. MUI's Dialog supplies the
 * rest of the behaviour — Escape, backdrop click, and focus returning to the eye icon that opened
 * it — so none of the three is re-implemented here.
 */
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Dialog, DialogContent, DialogTitle, IconButton, Skeleton } from "@mui/material";
import EmailPreview from "@/components/campaignBuilder/design/EmailPreview";
import { COPY } from "@/constants/adminCampaigns";
import { COPY as SHARED_CONTACTS_COPY } from "@/constants/campaignContacts";
import type { Recipient, Stage3EmailState } from "@/interfaces/CampaignBuilder";
import { apiErrorMessage } from "@/utils/apiError-helper";

interface ContactEmailPreviewModalProps {
  /** The campaign's client organisation — the subject and sender lines both name it. */
  organization: string;
  /** null while no row is selected; `open` is what actually gates the request. */
  contact: { id: number; name: string; email: string } | null;
  open: boolean;
  /** Closes this preview only — never the contacts modal behind it. */
  onClose: () => void;
  /** The campaign's email, read by the contacts modal from its draft endpoint. */
  preview: {
    data: Stage3EmailState | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  /**
   * GFG-364: that payload is not server-personalised, so the caller supplies the recipient and
   * `EmailPreview` resolves the @tokens on the client. Omitted, EmailPreview's own default applies.
   */
  recipients?: readonly Recipient[];
}

export default function ContactEmailPreviewModal({
  organization,
  contact,
  open,
  onClose,
  preview,
  recipients,
}: ContactEmailPreviewModalProps) {
  const { data, isLoading, isError, error } = preview;
  // A null contact disables the query, a state none of isLoading/isError/data reports on its own.
  const isPending = isLoading || contact === null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle component="div">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-[16px] font-bold text-rodeo-black">
              {contact === null ? "" : COPY.emailPreview.to(contact.name, contact.email)}
            </p>
            {/* The subject is rendered once, by EmailPreview, from the server's own payload —
                a second local copy of it drifted on any name the server split differently. */}
            <p className="mt-1 truncate text-[12px] text-graphite">
              {COPY.emailPreview.sender(organization)}
            </p>
          </div>
          <IconButton onClick={onClose} aria-label={SHARED_CONTACTS_COPY.closeLabel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent
        className="bg-wax flex justify-center items-start"
        sx={{ minHeight: 420, py: 4 }}
      >
        {isPending && (
          <div
            role="status"
            aria-busy="true"
            aria-label={SHARED_CONTACTS_COPY.loadingLabel}
            className="w-full max-w-[600px] flex flex-col gap-4"
          >
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={130} />
            <Skeleton variant="rounded" height={260} />
          </div>
        )}
        {!isPending && isError && (
          <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
            {apiErrorMessage(error)}
          </Alert>
        )}
        {!isPending && !isError && data && (
          <EmailPreview state={data} isMobile={false} recipients={recipients} />
        )}
      </DialogContent>
    </Dialog>
  );
}
